import {
  useEffect, useRef, useState,
} from 'react';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { RangeType } from '~/_shared/utils/range/range.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { BoundaryTerritory } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID, isBoundaryTerritorySpecial,
} from '../boundarySettings.helpers';
import { getBoundaryTerritoryRangeValues } from './boundarySettingsNumeric.helpers';

export const adjustRangeMinMaxForExistingBoundaryTerritories = (
  min: number | typeof BOUNDARY_MIN_VALUE_ID,
  max: number | typeof BOUNDARY_MAX_VALUE_ID,
  boundaryTerritories: readonly BoundaryTerritory[],
) => {
  const rangeBoundaryTerritories = boundaryTerritories.filter(t => !isBoundaryTerritorySpecial(t));

  if (!rangeBoundaryTerritories.length) {
    return { adjustedMin: min, adjustedMax: max };
  }

  let adjustedMin = min;

  if (min !== BOUNDARY_MIN_VALUE_ID) {
    const first = rangeBoundaryTerritories[0];
    const firstValues = first && getBoundaryTerritoryRangeValues(first);
    const valueTo: number = notNullsy(firstValues?.valueTo) ? Number(firstValues.valueTo) : NaN;
    adjustedMin = !isNaN(valueTo) && valueTo < min ? valueTo : min;
  }

  let adjustedMax = max;

  if (max !== BOUNDARY_MAX_VALUE_ID) {
    const last = rangeBoundaryTerritories.lastItem;
    const lastValues = getBoundaryTerritoryRangeValues(last);
    const valueFrom: number = notNullsy(lastValues?.valueFrom) ? Number(lastValues.valueFrom) : NaN;
    adjustedMax = !isNaN(valueFrom) && valueFrom > max ? valueFrom : max;
  }

  return { adjustedMin, adjustedMax };
};

// initializes state adjusted for territories already existing outside of the given min/max bounds,
// and keeps it in sync with min/max/rangeType changes (percentage range type always uses a fixed 0-100 range)
export const useAdjustedBoundaryRangeMinMax = (
  min: number | typeof BOUNDARY_MIN_VALUE_ID,
  max: number | typeof BOUNDARY_MAX_VALUE_ID,
  boundaryTerritories: readonly BoundaryTerritory[],
  rangeType: RangeType,
) => {
  const prevRangeType = usePrevious(rangeType);

  const [{ adjustedMin, adjustedMax }, setAdjustedMinMax] = useState(
    () => adjustRangeMinMaxForExistingBoundaryTerritories(min, max, boundaryTerritories),
  );

  const boundaryTerritoriesRef = useRef(boundaryTerritories);
  boundaryTerritoriesRef.current = boundaryTerritories;
  useEffect(() => {
    // on a rangeType flip, boundaryTerritories are still scaled for the previous rangeType (e.g. 0-100
    // for percentage) until the caller rescales them, so skip the territory-based adjustment and use the
    // raw props instead - once rescaled, territories will exactly fit min/max anyway
    const adjustedBounds = rangeType !== prevRangeType
      ? { adjustedMax: max, adjustedMin: min }
      : adjustRangeMinMaxForExistingBoundaryTerritories(min, max, boundaryTerritoriesRef.current);
    setAdjustedMinMax(adjustedBounds);
  }, [prevRangeType, max, min, rangeType]);

  const rangeMin = rangeType === RangeType.Value ? adjustedMin : 0;
  const rangeMax = rangeType === RangeType.Value ? adjustedMax : 100;

  return { rangeMin, rangeMax };
};
